// Shared DTOs between server and web. Kept dependency-free so both packages can
// import this file via a relative path without any build step.

export type DifficultySlug =
  | 'featherweight'
  | 'beginner'
  | 'medium'
  | 'advanced'
  | 'brainmelter';

export interface Difficulty {
  id: number;
  slug: DifficultySlug;
  name: string;
  position: number;
}

export interface Category {
  id: number;
  slug: string;
  name: string;
  emoji: string;
  weight: number;
}

export type RoundPhase = 'question' | 'reveal';

/** Public projection of a round — no `correct` answer index leaks during the question phase. */
export interface PublicRound {
  /** Round id, monotonically increasing per server start. */
  roundId: number;
  /** ISO timestamp when this round started (question phase began). */
  startedAt: string;
  /** ISO timestamp when the question phase ends and reveal begins. */
  endsAt: string;
  /** ISO timestamp when the reveal phase ends and the next round starts. */
  revealEndsAt: string;
  phase: RoundPhase;
  question: {
    id: number;
    prompt: string;
    answers: Array<{ id: number; content: string; position: number }>;
    category: { slug: string; name: string; emoji: string };
    difficulty: { slug: DifficultySlug; name: string; position: number };
  };
  /** Total live participants currently connected. */
  participantCount: number;
  /** How many distinct players have answered so far this round. */
  answeredCount: number;
}

export interface RevealPayload {
  roundId: number;
  correctAnswerId: number;
  explanation: string | null;
  /** answerId -> count of users who picked it. Aggregated across all submissions. */
  distribution: Record<number, number>;
  totalAnswered: number;
  /** What this user got, if anything. Null if they didn't answer or aren't tracked. */
  yourResult: {
    answerId: number | null;
    isCorrect: boolean;
    pointsAwarded: number;
    streakAfter: number;
    streakBonus: number;
  } | null;
}

export interface MeResponse {
  browserId: string;
  user: {
    id: number;
    username: string;
    displayName: string;
    avatarColor: string;
    createdAt: string;
  } | null;
  /** Tally for the *current* identity (anonymous browser or logged-in user). */
  stats: UserStats;
  settings: UserSettings;
}

export interface UserSettings {
  soundEnabled: boolean;
  reduceMotion: boolean;
}

export interface UserStats {
  totalScore: number;
  correctCount: number;
  wrongCount: number;
  skippedCount: number;
  questionsSeen: number;
  accuracyPct: number;
  currentStreak: number;
  bestStreak: number;
  rank: number | null;
  totalRanked: number;
}

export interface CategoryBreakdown {
  category: Category;
  totalScore: number;
  correctCount: number;
  wrongCount: number;
  skippedCount: number;
  questionsSeen: number;
  accuracyPct: number;
}

export interface DifficultyBreakdown {
  difficulty: Difficulty;
  totalScore: number;
  correctCount: number;
  wrongCount: number;
  skippedCount: number;
  questionsSeen: number;
  accuracyPct: number;
}

export interface LeaderboardEntry {
  rank: number;
  userId: number | null;
  /** Login username when the entry is a registered user; lets the client link to their public stats page. */
  username: string | null;
  browserId: string | null;
  displayName: string;
  avatarColor: string;
  isYou: boolean;
  totalScore: number;
  correctCount: number;
  wrongCount: number;
  bestStreak: number;
  currentStreak: number;
  questionsSeen: number;
  accuracyPct: number;
}

export type LeaderboardScope =
  | 'all-time'
  | 'today'
  | 'week'
  | 'best-streak';

export interface LeaderboardResponse {
  scope: LeaderboardScope;
  categorySlug: string | null;
  difficultySlug: DifficultySlug | null;
  entries: LeaderboardEntry[];
  yourEntry: LeaderboardEntry | null;
  totalRanked: number;
}

// ---- socket events ----

export interface ServerToClientEvents {
  /** Sent when a new round begins (question phase). */
  'round:start': (payload: PublicRound) => void;
  /** Sent every ~1s during the question phase with live counters. */
  'round:tick': (payload: { roundId: number; participantCount: number; answeredCount: number }) => void;
  /** Sent when the question phase ends and reveal begins. */
  'round:reveal': (payload: RevealPayload) => void;
  /** Push a fresh stats snapshot to a single client (e.g. after their answer was scored). */
  'me:stats': (stats: UserStats) => void;
  /** Compact mini-leaderboard refreshed each round. */
  'leaderboard:mini': (payload: { entries: LeaderboardEntry[]; yourEntry: LeaderboardEntry | null }) => void;
}

export interface ClientToServerEvents {
  'answer:submit': (
    args: { roundId: number; answerId: number },
    ack?: (res: { ok: boolean; error?: string }) => void,
  ) => void;
}

export const ROUND_QUESTION_MS = 20_000;
export const ROUND_REVEAL_MS = 5_000;
export const ROUND_TOTAL_MS = ROUND_QUESTION_MS + ROUND_REVEAL_MS;

/**
 * Streak milestone bonuses. Awarded once each time a user's correct streak first reaches the
 * milestone. Spaced every 2 correct answers so the dopamine arrives early and often — the full
 * +21 bonus stack is paid out by streak 10 (after which only the base difficulty reward applies).
 */
export const STREAK_MILESTONES: Array<{ at: number; bonus: number }> = [
  { at: 2, bonus: 1 },
  { at: 4, bonus: 2 },
  { at: 6, bonus: 3 },
  { at: 8, bonus: 5 },
  { at: 10, bonus: 10 },
];

/**
 * Per-difficulty configuration shared between server and client.
 *
 * - `weight` controls how often a difficulty is sampled. The 4·4·3·2·1 split keeps the bulk
 *   of rounds at the easier end so casual play doesn't feel punishing — Brainmelter still
 *   appears, just rarely (≈7% of rounds).
 * - `pointsCorrect` is the base reward for a correct answer at this difficulty. Wrong is
 *   always -1 and skip is always 0 regardless of difficulty.
 */
export const DIFFICULTY_RUNGS: Array<{
  slug: DifficultySlug;
  name: string;
  position: number;
  weight: number;
  pointsCorrect: number;
}> = [
  { slug: 'featherweight', name: 'Featherweight', position: 1, weight: 4, pointsCorrect: 1 },
  { slug: 'beginner',      name: 'Beginner',      position: 2, weight: 4, pointsCorrect: 2 },
  { slug: 'medium',        name: 'Medium',        position: 3, weight: 3, pointsCorrect: 3 },
  { slug: 'advanced',      name: 'Advanced',      position: 4, weight: 2, pointsCorrect: 4 },
  { slug: 'brainmelter',   name: 'Brainmelter',   position: 5, weight: 1, pointsCorrect: 5 },
];

export function pointsForCorrectAt(position: number): number {
  const r = DIFFICULTY_RUNGS.find((d) => d.position === position);
  return r ? r.pointsCorrect : 1;
}
